/**
 * Human-readable summary of a live game's runtime state, derived purely from the
 * already-watched `game.state` ({ currentState, roundState, round, questionId }).
 * Shared by the header status-pill subline and the Game Home "Live now" card —
 * no extra Firebase reads.
 */

const PHASE = {
  waiting: "Selecting a clue",
  "round.intro": "Round intro",
  "question.selected": "Selecting clue",
  "question.reading": "Reading clue",
  "question.answering": "Players answering",
  "question.judging": "Judging",
  "question.results": "Results",
  "question.revealing": "Revealing",
  "round.complete": "Round complete",
  "dailyDouble.picked": "Daily Double",
  "question.wagering": "Wagering",
  "final.preview": "Final — preview",
  "final.wagerInstructions": "Final — wagers",
  "final.questionIntro": "Final clue",
  "final.revealing": "Final reveal",
};

export const roundLabel = (round) => {
  if (!round) return "";
  if (round === "final") return "Final Jeopardy";
  if (round === "practice") return "Warm-up";
  if (/^round\d+$/.test(round)) return `Round ${round.slice(5)}`;
  return round;
};

/** Name of the player currently in control (i.e. selecting a clue), or "". */
export const controllingPlayerName = (game, players = []) => {
  const id = game?.state?.playerInControl;
  if (!id) return "";
  const p = players.find((pl) => pl?.id === id);
  return p ? p.displayName || p.name || "" : "";
};

/**
 * Short one-line label, e.g. "Round 2 • Judging", "Lobby", "Finale". While the
 * board is up waiting on a clue pick, pass `selectingPlayer` to name who's on
 * the clock ("Waiting for Dana to select a clue"). Falls back to "In progress"
 * when state is sparse.
 */
export const liveStateLabel = (game, { selectingPlayer = "" } = {}) => {
  const s = game?.state || {};
  switch (s.currentState) {
    case "pregame":
      return "Not started";
    case "lobby":
      return "Lobby";
    case "instructions":
      return "Instructions";
    case "credits":
      return "Finale";
    default:
      break;
  }
  const r = roundLabel(s.round);
  const phase =
    s.roundState === "waiting" && selectingPlayer
      ? `Waiting for ${selectingPlayer} to select a clue`
      : PHASE[s.roundState];
  if (r && phase) return `${r} • ${phase}`;
  return r || phase || "In progress";
};
